import React, { useEffect, useState, Suspense, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { generateRandomColors } from './utils/randomColors';
import { ThemeProvider } from './contexts/ThemeContext';
import { ThemeToggle } from './components/ThemeToggle';
import ErrorBoundary from './components/ErrorBoundary';
import PhoneReveal from './components/PhoneReveal';
import './App.css';

// Lazy load pages for better performance
const preloadAbout = () => import('./pages/About');
const About = React.lazy(preloadAbout);
const preloadArticles = () => import('./pages/Articles');
const Articles = React.lazy(preloadArticles);
const preloadStats = () => import('./pages/Stats');
const Stats = React.lazy(preloadStats);
const preloadPoems = () => import('./pages/poems');
const Poems = React.lazy(preloadPoems);
const preloadAiMlDeveloper = () => import('./pages/AiMlDeveloper');
const AiMlDeveloper = React.lazy(preloadAiMlDeveloper);
const preloadGameDeveloper = () => import('./pages/GameDeveloper');
const GameDeveloper = React.lazy(preloadGameDeveloper);
const preloadQuantumProgrammer = () => import('./pages/QuantumProgrammer');
const QuantumProgrammer = React.lazy(preloadQuantumProgrammer);

// Loading component
const LoadingSpinner = () => (
  <div className="loading-container">
    <div className="loading-spinner"></div>
    <p>Loading...</p>
  </div>
);

function MainContent() {
  const [showMessage, setShowMessage] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Certificate filtering functionality with multiple selection support
  useEffect(() => {
    const filterButtons = document.querySelectorAll('.filter-btn');
    const certificates = document.querySelectorAll('.certificates-list li');

    const handleFilterClick = (e: Event) => {
      const button = e.target as HTMLButtonElement;
      const filter = button.getAttribute('data-filter');

      // Handle "All" button
      if (filter === 'all') {
        filterButtons.forEach(btn => btn.classList.remove('active'));
        button.classList.add('active');
        certificates.forEach(cert => {
          (cert as HTMLElement).style.display = '';
        });
        return;
      }

      // Toggle active state for other buttons
      const allButton = document.querySelector('.filter-btn[data-filter="all"]');
      allButton?.classList.remove('active');
      button.classList.toggle('active');

      // Get all active filters (excluding "all")
      const activeFilters: string[] = [];
      filterButtons.forEach(btn => {
        if (btn.classList.contains('active') && btn.getAttribute('data-filter') !== 'all') {
          activeFilters.push(btn.getAttribute('data-filter') || '');
        }
      });

      // If no filters are active, activate "All"
      if (activeFilters.length === 0) {
        allButton?.classList.add('active');
        certificates.forEach(cert => {
          (cert as HTMLElement).style.display = '';
        });
        return;
      }

      // Filter certificates based on active filters
      certificates.forEach(cert => {
        const categories = cert.getAttribute('data-category')?.split(' ') || [];
        const hasMatch = activeFilters.some(filter => categories.includes(filter));
        
        (cert as HTMLElement).style.display = hasMatch ? '' : 'none';
      });
    };

    filterButtons.forEach(btn => {
      btn.addEventListener('click', handleFilterClick);
    });

    // Cleanup
    return () => {
      filterButtons.forEach(btn => {
        btn.removeEventListener('click', handleFilterClick);
      });
    };
  }, []);

const handleCVDownload = useCallback((type: string) => {
  setIsDropdownOpen(false);

  if (type === 'quantum') {
    try {
      const link = document.createElement('a');
      link.href = '/assets/CV/Redwan Rahman Quantum CV.pdf';
      link.download = 'Redwan Rahman Quantum CV.pdf';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading CV:', error);
      setShowMessage(true);
      setTimeout(() => setShowMessage(false), 2000);
    }
  } else if (type === 'aiml') {
    try {
      const link = document.createElement('a');
      link.href = '/assets/CV/Redwan Rahman AIML CV.pdf';
      link.download = 'Redwan Rahman AIML CV.pdf';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading CV:', error);
      setShowMessage(true);
      setTimeout(() => setShowMessage(false), 2000);
    }
  } else {
    setShowMessage(true);
    setTimeout(() => setShowMessage(false), 2000);
  }
}, []);


  const toggleDropdown = useCallback(() => {
    setIsDropdownOpen(prev => !prev);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const dropdown = document.querySelector('.cv-dropdown-container');
      if (dropdown && !dropdown.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  return (
    <div className="App">
      {/* Theme Toggle */}
      <ThemeToggle />
      
      {/* CV Dropdown Button */}
      <div className="cv-dropdown-container">
        <button 
          className="cv-dropdown-button" 
          onClick={toggleDropdown}
          aria-expanded={isDropdownOpen}
          aria-haspopup="true"
        >
          Download CV
          <span style={{ 
            transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s ease',
            display: 'inline-block'
          }}>
            ▼
          </span>
        </button>
        
        {isDropdownOpen && (
          <div className="cv-dropdown-menu" role="menu">
            <button 
              className="cv-dropdown-item" 
              onClick={() => handleCVDownload('aiml')}
              role="menuitem"
            >
              AI/ML Developer
            </button>
            <button 
              className="cv-dropdown-item" 
              onClick={() => handleCVDownload('quantum')}
              role="menuitem"
            >
              Quantum Developer
            </button>
            <button 
              className="cv-dropdown-item" 
              onClick={() => handleCVDownload('game')}
              role="menuitem"
            >
              Game Developer
            </button>
          </div>
        )}
      </div>

      {/* Message Overlay */}
      {showMessage && (
        <div className="message-overlay" onClick={() => setShowMessage(false)}>
          <div className="message-content" onClick={(e) => e.stopPropagation()}>
            Content has not uploaded yet
          </div>
        </div>
      )}

      <header className="hero">
        <div className="hero-main-content">
          <div className="profile-container">
            <img
              src="/assets/images/My Formal Pic.jpg"
              alt="Redwan Rahman"
              className="profile-picture"
              decoding="async"
              fetchPriority="high"
            />
          </div>
          <h1>Redwan Rahman</h1>
          <div className="hero-buttons">
            <Link to="/about" className="about-button" onMouseEnter={preloadAbout}>About Me</Link>
            <Link to="/articles" className="articles-button" onMouseEnter={preloadArticles}>My Articles</Link>
            <Link to="/stats" className="stats-button" onMouseEnter={preloadStats}>Stats</Link>
          </div>
          <div className="contact-info">
            <p><strong>Email:</strong> <a href="mailto:rahman22205101127@diu.edu.bd">rahman22205101127@diu.edu.bd</a> | <a href="mailto:redwanrahman2002@outlook.com">redwanrahman2002@outlook.com</a></p>
            <p><PhoneReveal /></p>
            <p><strong>GitHub:</strong> <a href="https://github.com/Red1-Rahman" target="_blank" rel="noopener noreferrer">Red1-Rahman</a></p>
            <p><strong>LinkedIn:</strong> <a href="https://www.linkedin.com/in/redwan-rahman-13098a34b/" target="_blank" rel="noopener noreferrer">linkedin.com/in/redwan-rahman-13098a34b</a></p>
          </div>
        </div>
        <div className="marquee-container">
          <div className="marquee-content">
            <Link to="/ai-ml" className="marquee-link">AI/ML Developer</Link> • <Link to="/game-dev" className="marquee-link">Game Developer</Link> • <Link to="/quantum" className="marquee-link">Quantum Programmer</Link>
          </div>
        </div>
      </header>

      <section className="section" id="skills">
        <h2>My Expertise</h2>
        <p className="section-subtitle">Explore my specialized domains</p>
        
        <div className="domain-navigation">
          <Link to="/ai-ml" className="domain-nav-card ai-ml" onMouseEnter={preloadAiMlDeveloper}>
            <div className="domain-icon">🤖</div>
            <h3>AI/ML Developer</h3>
            <p>Machine Learning • Deep Learning • Data Science</p>
            <div className="nav-arrow">→</div>
          </Link>
          
          <Link to="/game-dev" className="domain-nav-card game-dev" onMouseEnter={preloadGameDeveloper}>
            <div className="domain-icon">🎮</div>
            <h3>Game Developer</h3>
            <p>Unreal Engine • Unity • Interactive Experiences</p>
            <div className="nav-arrow">→</div>
          </Link>
          
          <Link to="/quantum" className="domain-nav-card quantum" onMouseEnter={preloadQuantumProgrammer}>
            <div className="domain-icon">⚛️</div>
            <h3>Quantum Programmer</h3>
            <p>Quantum Computing • QML • Astrophysics Research</p>
            <div className="nav-arrow">→</div>
          </Link>
        </div>
      </section>

      <section className="section" id="experience">
        <h2>Experience</h2>
        <ul>
          <li>
            <strong>Artificial Intelligence Intern - CodeAlpha (1st April 2026 - 30th April 2026)</strong>
            <p>Developed NLP powered support bot (<a href="https://github.com/Red1-Rahman/CodeAlpha_astro-ai-chatbot" target="_blank" rel="noopener noreferrer">GitHub</a>). Developed simple translator leveraging both Google and Azure Translator APIs for multi-language support (<a href="https://github.com/Red1-Rahman/CodeAlpha_Language-Translator" target="_blank" rel="noopener noreferrer">GitHub</a>).</p>
          </li>
          <li>
            <strong>Machine Learning Intern - CodeAlpha (25th March 2026 - 25th June 2026)</strong>
            <p>Building and implementing machine learning solutions with focus on model development, optimization, and real-world deployment of intelligent systems.</p>
          </li>
          <li>
            <strong>Mentor - QBangladesh (August 2025 - Present)</strong>
            <p>Mentored <a href="https://qworld.net/qsilver31/" target="_blank" rel="noopener noreferrer">QSilver31</a> intermediate quantum workshop (4-day, 100+ global participants), teaching Quantum Fourier Transform, Shor's Algorithm, and Qiskit programming with 95% completion rate. Delivered outreach presentations increasing quantum technology awareness among Bangladeshi and international students.</p>
          </li>
          <li>
            <strong>President (Student Forum) - DIU Astrophysics Center (March 2025 - Present)</strong>
            <p>Led 270+ members (20 forum members, 250+ general), organizing 5+ technical events on computational astrophysics, gravitational wave analysis, and space science. Managed cross-functional teams for collaborative research in galaxy evolution, JWST data analysis, and astronomical machine learning. Coordinated outreach programs and educational initiatives to promote astrophysics research.</p>
          </li>
          <li>
            <strong>General Secretary - AR/VR and Game Development Lab_DIU (May 2025 - September 2025)</strong>
            <p>Managed lab operations with 12+ executives, coordinating AR/VR initiatives, overseeing 10+ projects; organized 4+ workshops on Unity (C#), Unreal Engine training 50+ students. Previously Vice President of Development Wing, mentoring developers in augmented reality, virtual reality, and game development.</p>
          </li>
        </ul>
      </section>


      <section className="section" id="projects">
        <h2>Projects</h2>
        <ul>
          <li>
            <a
              href="https://itch.io/jam/quantum-game-jam-2025/rate/3867893"
              target="_blank"
              rel="noopener noreferrer"
              className="project-link"
            >
              DraQ: Astro-Escape
            </a>
            <br />
            <span className="project-badge">Quantum GameJam 2025</span>
            <span className="project-badge">Unreal Engine 5</span>
          </li>
          <li>
            <a
              href="https://r3ns-astro-ai.streamlit.app/"
              target="_blank"
              rel="noopener noreferrer"
              className="project-link"
            >
              Astro-AI: Unified AI-Powered Astrophysics Analysis Platform
            </a>
            <br />
            <strong>AI-integrated galaxy evolution platform with 21cmFAST simulations, JWST spectral analysis, Bagpipes SED fitting, and automated scientific reporting</strong>
            <br />
            <span className="project-badge">Python</span>
            <span className="project-badge">Streamlit</span>
            <span className="project-badge">21cmFAST</span>
            <span className="project-badge">JWST Pipeline</span>
            <span className="project-badge">Bagpipes</span>
            <span className="project-badge">DeepSeek R1</span>
          </li>
          <li>
            <a
              href="https://gravitational-wave-parameter-estimator-demo.streamlit.app/"
              target="_blank"
              rel="noopener noreferrer"
              className="project-link"
            >
              Gravitational Wave Parameter Estimator
            </a>
            <br />
            <strong>Fast and Uncertainty-Aware Parameter Estimation with Transformers, trained on 800 synthetic binary black hole waveforms with realistic detector noise</strong>
          </li>
          <li>
            <a
              href="https://www.spaceappschallenge.org/nasa-space-apps-2024/find-a-team/the-quadragon/?tab=project"
              target="_blank"
              rel="noopener noreferrer"
              className="project-link"
            >
              LumiSafe
            </a>
            <br />
            <strong>Mobile application providing personalized information and insights related to solar activity and Earth's climate, leveraging NASA's Earth observation data and advanced AI algorithms</strong>
            <br />
            <span className="project-badge">NASA Space Apps Challenge 2024</span>
            <span className="project-badge">Galactic Problem Solver</span>
          </li>
          <li>
            <a
              href="https://github.com/Red1-Rahman/muslim-buddy"
              target="_blank"
              rel="noopener noreferrer"
              className="project-link"
            >
              Muslim Buddy
            </a>
            <br />
            <strong>A comprehensive Laravel web application for Muslims to track their spiritual journey, featuring prayer times calculation using astronomical formulas, Quran progress tracking, and community leaderboards</strong>
            <br />
            <span className="project-badge">Laravel</span>
            <span className="project-badge">PHP</span>
          </li>
          <li>
            <a
              href="https://github.com/Red1-Rahman/Sanatan-Scriptures"
              target="_blank"
              rel="noopener noreferrer"
              className="project-link"
            >
              Sanatan Scriptures
            </a>
            <br />
            <strong>Hindu Religious Texts Learning Platform</strong>
          </li>
          <li>
            <a
              href="https://scilearnplatform.free.nf/"
              target="_blank"
              rel="noopener noreferrer"
              className="project-link"
            >
              SciLearn Platform
            </a>
            <br />
            <strong>Science Learning Platform</strong>
          </li>
          <li>
            <a
              href="https://quantum-galton-board-generator.netlify.app/"
              target="_blank"
              rel="noopener noreferrer"
              className="project-link"
            >
              Quantum Galton Board Generator
            </a>
          </li>
          <li>
            <strong>Shell-Based Mini Operating System</strong>
            <br />
            A terminal-driven mini operating system using Bash scripting, featuring user authentication, process and file management, system monitoring, networking tools, user simulation, DHCP logic, and a built-in mini-game
          </li>
          <li>
            <strong>Mini Game Script Interpreter</strong>
            <br />
            A domain-specific scripting language interpreter for building simple 2D grid-based games
          </li>
          <li>
            <strong>Gradio-Based MBTI Personality Prediction Web App Using an Ensemble Model</strong>
            <br />
            <span className="project-accuracy">91.05% Accuracy</span>
          </li>
          <li>
            <strong>ML based astronomical object classifier</strong>
          </li>
          <li>
            <strong>MRI-Based Brain Tumor Diagnosis Using YOLOv8 for Detection and DenseNet201 for Classification</strong>
            <br />
            <span className="project-accuracy">mAP50: 88.2%, Accuracy: 86.05%</span>
          </li>
          <li>
            <a
              href="https://connect-4-with-ai-opponent.netlify.app"
              target="_blank"
              rel="noopener noreferrer"
              className="project-link"
            >
              Board games with AI opponents
            </a>
          </li>
          <li>
            <a
              href="https://computer-graphics.streamlit.app/"
              target="_blank"
              rel="noopener noreferrer"
              className="project-link"
            >
              Computer Graphics Algorithm Visualizer
            </a>
            <br />
            <strong>Interactive visualizer for computer graphics algorithms with learning resources</strong>
            <br />
            <span className="project-badge">Python</span>
            <span className="project-badge">Streamlit</span>
            <span className="project-badge">DDA</span>
            <span className="project-badge">Bresenham</span>
            <span className="project-badge">2D/3D Transformations</span>
          </li>
        </ul>
      </section>

<section className="section" id="certificates">
  <h2>Certificates</h2>
  
  <div className="filter-buttons">
    <button className="filter-btn active" data-filter="all">All</button>
    <button className="filter-btn" data-filter="ai">AI</button>
    <button className="filter-btn" data-filter="ml">ML</button>
    <button className="filter-btn" data-filter="quantum">Quantum</button>
    <button className="filter-btn" data-filter="web">Web</button>
    <button className="filter-btn" data-filter="astro">Astro</button>
    <button className="filter-btn" data-filter="gamedev">GameDev</button>
    <button className="filter-btn" data-filter="others">Others</button>
  </div>

  <ul className="certificates-list">
    <li data-category="ai">
      <a
        href="https://huggingface.co"
        target="_blank"
        rel="noopener noreferrer"
        className="certificate-link"
      >
        The Fundamentals of LLMs - HuggingFace
      </a>
      <span className="cert-date"> (February 9th, 2026)</span>
    </li>
    <li data-category="ai web">
      <a
        href="https://verify.skilljar.com/c/5rkmevk5o5xf"
        target="_blank"
        rel="noopener noreferrer"
        className="certificate-link"
      >
        Build workflows with Zapier Tables - Zapier
      </a>
      <span className="cert-date"> (February 9th, 2026)</span>
    </li>
    <li data-category="ai">
      <a
        href="https://edu.exceedlms.com/student/award/3NNVYEzLsa2DB2rJjZJgKuNx"
        target="_blank"
        rel="noopener noreferrer"
        className="certificate-link"
      >
        Generative AI for Educators Certificate - Google
      </a>
      <span className="cert-date"> (December 7th, 2025)</span>
    </li>
    <li data-category="astro">
      <a
        href="https://www.credly.com/badges/377d8a95-736d-4f2c-8f30-7184e4f66b68/linked_in_profile"
        target="_blank"
        rel="noopener noreferrer"
        className="certificate-link"
      >
        NASA Open Science 101 - NASA
      </a>
      <span className="cert-date"> (December 3rd, 2025)</span>
    </li>
    <li data-category="quantum">
      <a
        href="https://lms.qureca.com/certificate-verifier/?code=3763-176-686-1212&v=c419b06b4c65"
        target="_blank"
        rel="noopener noreferrer"
        className="certificate-link"
      >
        ADEQUATE Certificate - QURECA
      </a>
      <span className="cert-date"> (December 27th, 2025)</span>
    </li>
    <li data-category="web">
      <a
        href="https://badges.parchment.com/public/assertions/VNBOFN8oTbedS7HStAnHfg?identity__email=rahman22205101127@diu.edu.bd"
        target="_blank"
        rel="noopener noreferrer"
        className="certificate-link"
      >
        Postman API Fundamentals Student Expert - Postman
      </a>
      <span className="cert-date"> (November 22nd, 2025)</span>
    </li>
    <li data-category="quantum ml">QBarsaat2025: Quantum Machine Learning - QWorld and QPakistan</li>
    <li data-category="quantum">QCobalt8 Diploma - QWorld and QPakistan</li>
    <li>
      <a
        href="https://ti-user-certificates.s3.amazonaws.com/e0df7fbf-a057-42af-8a1f-590912be5460/a2ebdb61-5527-4a16-a121-26b83cf6f73e-redwan-rahman-781b4032-5bf6-47fd-8729-e5b546e173da-certificate.pdf"
        target="_blank"
        rel="noopener noreferrer"
        className="certificate-link"
      >
        Authentication and Authorization for Web/API - The Linux Foundation
      </a>
      <span className="cert-date"> (September 13th, 2025)</span>
    </li>
    <li data-category="astro">CAM-SUST Summer School on Astrophysics 2025 - Copernicus Astronomical Memorial of SUST <span className="cert-date">(September 2025)</span></li>
    <li data-category="ai astro">
      <a
        href="https://learn.nvidia.com/certificates?id=D82IIr-3R_ehRMujdbDezQ#"
        target="_blank"
        rel="noopener noreferrer"
        className="certificate-link"
      >
        Disaster Risk Monitoring Using Satellite Imagery - NVIDIA
      </a>
      <span className="cert-date"> (September 4th, 2025)</span>
    </li>
    <li data-category="quantum">
      <a
        href="https://www.virtualbadge.io/certificate-validator?credential=1d93a655-ec42-4131-ac47-2ccfe1bca6d9"
        target="_blank"
        rel="noopener noreferrer"
        className="certificate-link"
      >
        2025 Quantum Program - The Washington Institute for STEM, Entrepreneurship and Research
      </a>
      <span className="cert-date"> (August 21st, 2025)</span>
    </li>
    <li data-category="quantum">
      <a
        href="https://pennylane.ai/profile/Redwan_Rahman?certificate=pennylane-certificate-womanium-wiser-trotterization-2025"
        target="_blank"
        rel="noopener noreferrer"
        className="certificate-link"
      >
        Trotterization Challenge - PennyLane
      </a>
      <span className="cert-date"> (August 7th, 2025)</span>
    </li>
    <li data-category="quantum">
      <a
        href="https://www.virtualbadge.io/certificate-validator?credential=be6b810f-f341-46c3-8cc5-0532e058aed3"
        target="_blank"
        rel="noopener noreferrer"
        className="certificate-link"
      >
        Project Certificate: Quantum Walks and Monte Carlo - The Washington Institute for STEM, Entrepreneurship and Research
      </a>
      <span className="cert-date"> (August 28th, 2025)</span>
    </li>
    <li data-category="ml">
      <a
        href="https://www.credly.com/badges/78b2c935-e31d-4927-b2e4-3a9fdb273559/public_url"
        target="_blank"
        rel="noopener noreferrer"
        className="certificate-link"
      >
        AWS Academy Graduate Machine Learning Foundations - Amazon AWS
      </a>
      <span className="cert-date"> (July 15th, 2025)</span>
    </li>
    <li data-category="ai ml gamedev">
      <a
        href="https://www.kaggle.com/learn/certification/redwanrahman1127/intro-to-game-ai-and-reinforcement-learning"
        target="_blank"
        rel="noopener noreferrer"
        className="certificate-link"
      >
        Intro to Game AI and Reinforcement Learning - Kaggle
      </a>
      <span className="cert-date"> (July 2nd, 2025)</span>
    </li>
    <li data-category="quantum">
      <a
        href="https://pennylane.ai/profile/Redwan_Rahman?certificate=pennylane-certificate-womanium-wiser-lcu-2025"
        target="_blank"
        rel="noopener noreferrer"
        className="certificate-link"
      >
        LCU Challenge - Pennylane
      </a>
      <span className="cert-date"> (July 31st, 2025)</span>
    </li>
    <li data-category="quantum">QBronze169 Diploma - QWorld <span className="cert-date">(July 2025)</span></li>
    <li>
      <a
        href="https://coursera.org/share/18e299932ad4ef3b99c044574f155265"
        target="_blank"
        rel="noopener noreferrer"
        className="certificate-link"
      >
        Feature Engineering - Google
      </a>
      <span className="cert-date"> (June 13th, 2025)</span>
    </li>
    <li data-category="ml">
      <a
        href="https://coursera.org/share/bbd7cc45b7923ab8aebe6590a6d46db1"
        target="_blank"
        rel="noopener noreferrer"
        className="certificate-link"
      >
        Deep Learning with Keras and TensorFlow - IBM
      </a>
      <span className="cert-date"> (June 2nd, 2025)</span>
    </li>
    <li data-category="others">
      <a
        href="https://coursera.org/share/3ec38798eb25b6d1fd4f02318a7c9564"
        target="_blank"
        rel="noopener noreferrer"
        className="certificate-link"
      >
        Python Basics - University of Michigan
      </a>
      <span className="cert-date"> (May 3rd, 2025)</span>
    </li>
    <li data-category="ml">
      <a
        href="https://www.kaggle.com/learn/certification/redwanrahman1127/intro-to-machine-learning"
        target="_blank"
        rel="noopener noreferrer"
        className="certificate-link"
      >
        Intro to Machine Learning - Kaggle
      </a>
      <span className="cert-date"> (April 23rd, 2025)</span>
    </li>
    <li data-category="ml">
      <a
        href="https://coursera.org/share/6466ec5da646189a9c0be0a282599bcb"
        target="_blank"
        rel="noopener noreferrer"
        className="certificate-link"
      >
        Machine Learning with Python - IBM
      </a>
      <span className="cert-date"> (April 2nd, 2025)</span>
    </li>
    <li data-category="others">
      <a
        href="https://coursera.org/share/74044cdebfe2d219bd2848b9e2131dd1"
        target="_blank"
        rel="noopener noreferrer"
        className="certificate-link"
      >
        Data Science Math Skills - Duke University
      </a>
      <span className="cert-date"> (March 31st, 2025)</span>
    </li>
    <li data-category="ai">
      <a
        href="https://goedu.ac/certificates/b3d1f06331a18fe1efa7b0746b8df375/"
        target="_blank"
        rel="noopener noreferrer"
        className="certificate-link"
      >
        Optimization Using Artificial Intelligence: Hill Climbing Algorithm - GoEdu
      </a>
      <span className="cert-date"> (November 12th, 2024)</span>
    </li>
    <li data-category="others">
      <a
        href="https://drive.google.com/file/d/1qFi9uzHVqPymNano_tvPG7SgRUnlva-w/view"
        target="_blank"
        rel="noopener noreferrer"
        className="certificate-link"
      >
        Orange Corners Bangladesh Ideation Challenge 4.0 - Orange Corner Bangladesh
      </a>
      <span className="cert-date"> (November 2024)</span>
    </li>
    <li data-category="web">
      <a
        href="https://goedu.ac/certificates/47223679e93acfd97e18417813a224a5/"
        target="_blank"
        rel="noopener noreferrer"
        className="certificate-link"
      >
        Blockchain and Cryptocurrency Basic Knowledge - GoEdu
      </a>
      <span className="cert-date"> (October 23rd, 2024)</span>
    </li>
    <li data-category="astro">Galactic Problem Solver - NASA Space Apps Challenge 2024 <span className="cert-date">(October 2024)</span></li>
    <li data-category="others">Unlock The Algorithm Spring 2024 - Daffodil International University <span className="cert-date">(2024)</span></li>
    <li data-category="others">Take Off CSE Fest 2022 - Daffodil International University <span className="cert-date">(2022)</span></li>
    <li data-category="ai">
      <a
        href="https://github.com/Red1-Rahman/skills-getting-started-with-github-copilot"
        target="_blank"
        rel="noopener noreferrer"
        className="certificate-link"
      >
        Getting Started with GitHub Copilot - GitHub Skills
      </a>
      <span className="cert-date"> (2026)</span>
    </li>
    <li data-category="ai">
      <a
        href="https://github.com/Red1-Rahman/skills-customize-your-github-copilot-experience"
        target="_blank"
        rel="noopener noreferrer"
        className="certificate-link"
      >
        Customize Your GitHub Copilot Experience - GitHub Skills
      </a>
      <span className="cert-date"> (2026)</span>
    </li>
    <li data-category="ai">
      <a
        href="https://github.com/Red1-Rahman/skills-ai-in-actions"
        target="_blank"
        rel="noopener noreferrer"
        className="certificate-link"
      >
        AI in Actions - GitHub Skills
      </a>
      <span className="cert-date"> (2026)</span>
    </li>
    <li data-category="gamedev">
      <a
        href="https://cursa.app/en/my-certificate/cert0b72aa6bd33ff447f2a2d3a10a4058e2"
        target="_blank"
        rel="noopener noreferrer"
        className="certificate-link"
      >
        Intro to C++ Game Programming
      </a>
    </li>
    <li data-category="gamedev">
      <a
        href="https://cursa.app/en/my-certificate/cert19bec9533d2b4e0aee65ca50cf316ba9"
        target="_blank"
        rel="noopener noreferrer"
        className="certificate-link"
      >
        Unreal Engine 5 Complete Tutorials
      </a>
    </li>
    <li data-category="quantum">QURECA Basic Rules of Quantum Physics - Quantum Physics Foundation</li>
    <li data-category="quantum">QURECA Quantum Myths - Quantum Misconceptions & Facts</li>
  </ul>
</section>

      <section className="section" id="workshops">
        <h2>Workshops & Training Programs</h2>
        <ul>
          <li><strong>BeginPlay Unreal Engine Indie Sessions Workshop</strong> by Untold Games</li>
          <li><strong>WISER & Womanium Quantum Program 2025</strong> by The Washington Institute for STEM, Entrepreneurship and Research and QWorld</li>
          <li><strong>Stellar Light & Cosmic Bends: Data-Driven Astronomy Workshop</strong> organized by the DIU Astrophysics Center</li>
          <li><strong>Summer School on Astrophysics 2025</strong> by Copernicus Astronomical Memorial of SUST</li>
          <li><strong>Quantum Machine Learning - QBarsaat 2025</strong> by QWorld and QPakistan</li>
          <li><strong>Enhancing AR/VR and Game Development Skills</strong> by AR/VR & Game Development Lab_DIU</li>
          <li><strong>Want to Explore the World of Game Development?</strong> by AR/VR & Game Development Lab-DIU</li>
        </ul>
      </section>

      <section className="section" id="achievements">
        <h2>Competitions</h2>
        <ul>
          <li><strong>Finalist</strong> - Hult Prize 2025</li>
          <li><strong>Finalist</strong> - Orange Corners Ideation Challenge 4.0 (Nov 2024)</li>
          <li><strong>Finalist</strong> - Unlock The Algorithm Spring 2024, Daffodil International University</li>
          <li><strong>Finalist</strong> - Take Off Programming Contest 2022, Daffodil International University</li>
          <li><strong>Participant</strong> - Quantum GameJam 2025 (Project: "DraQ: Astro Escape" - quantum echo theme game)</li>
        </ul>
      </section>

      <section className="section" id="scholarships">
        <h2>Scholarships</h2>
        <ul>
          <li>UNDP FutureNation – Frontier Tech Scholarship</li>
          <li>Scholar: WISER & Womanium Quantum Solvers: Algorithms for the World's Hardest Problems</li>
        </ul>
      </section>

      <section className="section" id="patents">
        <h2>Research Datasets</h2>
        <ul>
          <li>
            <a
              href="https://data.mendeley.com/datasets/m7w55sw88b/1"
              target="_blank"
              rel="noopener noreferrer"
              className="patent-link"
            >
              PMRAM: Bangladeshi Brain Cancer - MRI Dataset
            </a>
            <p className="doi">DOI: 10.17632/m7w55sw88b.1</p>
          </li>
        </ul>
      </section>

      <section className="section" id="github-activity">
        <h2>GitHub Activity</h2>
        <div className="github-graph-container">
          <a
            href="https://github.com/Red1-Rahman"
            target="_blank"
            rel="noopener noreferrer"
            className="github-graph-link"
          >
            <img
              src="https://ghchart.rshah.org/Red1-Rahman"
              alt="Red1-Rahman GitHub Contribution Graph"
              className="github-contribution-graph"
            />
          </a>
        </div>
      </section>

      <footer className="footer">
        <p className="copyright">© 2025 Redwan Rahman. All rights reserved.</p>
      </footer>
    </div>
  );
}

function App() {
  useEffect(() => {
    const updateBackground = () => {
      document.body.style.background = generateRandomColors();
    };

    updateBackground();
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <Router>
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route path="/" element={<MainContent />} />
              <Route path="/about" element={<About />} />
              <Route path="/articles" element={<Articles />} />
              <Route path="/stats" element={<Stats />} />
              <Route path="/poems" element={<Poems />} />
              <Route path="/ai-ml" element={<AiMlDeveloper />} />
              <Route path="/game-dev" element={<GameDeveloper />} />
              <Route path="/quantum" element={<QuantumProgrammer />} />
            </Routes>
          </Suspense>
          <footer className="copyright-footer">
            <p>&copy; {new Date().getFullYear()} All rights reserved. Redwan Rahman</p>
          </footer>
        </Router>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
